import type { IClientRepository, Client, ClientStats } from '../interfaces/IClientRepository';
import { mockClients, simulateDelay, maybeSimulateError } from './mockData';

export class MockClientRepository implements IClientRepository {
  private clients: Client[] = [...mockClients];

  async getAll(filters?: {
    search?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ clients: Client[]; total: number }> {
    await simulateDelay();
    maybeSimulateError();

    let filtered = [...this.clients];

    // Apply search filter
    if (filters?.search) {
      const query = filters.search.toLowerCase();
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(query) ||
        t.id.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (filters?.status) {
      filtered = filtered.filter(t => t.status === filters.status);
    }

    const total = filtered.length;

    // Apply pagination
    const page = filters?.page || 1;
    const limit = filters?.limit || 10;
    const start = (page - 1) * limit;
    const paginated = filtered.slice(start, start + limit);

    return { clients: paginated, total };
  }

  async getById(id: string): Promise<Client | null> {
    await simulateDelay();
    maybeSimulateError();
    return this.clients.find(t => t.id === id) || null;
  }

  async getStats(clientId: string): Promise<ClientStats> {
    await simulateDelay();
    maybeSimulateError();
    
    const client = await this.getById(clientId);
    if (!client) throw new Error('Client not found');

    return {
      total_users: client.user_count,
      active_users: Math.floor(client.user_count * 0.8),
      pending_invitations: Math.floor(Math.random() * 5),
      onboarding_progress: client.onboarding_completed ? 100 : Math.floor(Math.random() * 75),
      campaigns_active: Math.floor(Math.random() * 10),
    };
  }

  async create(data: { name: string; created_by: string }): Promise<Client> {
    await simulateDelay();
    maybeSimulateError();

    const client: Client = {
      id: crypto.randomUUID(),
      name: data.name,
      status: 'active',
      created_at: new Date().toISOString(),
      onboarding_completed: false,
      admin_unlocked: false,
      user_count: 0,
    };

    this.clients.push(client);
    return client;
  }

  async update(id: string, data: Partial<Client>): Promise<Client> {
    await simulateDelay();
    maybeSimulateError();

    const index = this.clients.findIndex(t => t.id === id);
    if (index === -1) throw new Error('Client not found');

    this.clients[index] = { ...this.clients[index], ...data };
    return this.clients[index];
  }

  async suspend(id: string, reason: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();
    await this.update(id, { status: 'suspended' });
    // Client suspended
  }

  async reactivate(id: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();
    await this.update(id, { status: 'active' });
  }
}
